/**
 * Access gate shared by the onboarding preview harnesses — `DevOnboardingLauncher`
 * (`?ob=control|brand`) and the sibling `?preview…=1` harnesses
 * (`onboardingPreviewShared`, `BrandTasteOnboardingPreview`).
 *
 * Local dev (`next dev`) stays open for devs with no token — the harness there is
 * freely reachable, so there's nothing to protect. On every Vercel build — both
 * preview AND production — it unlocks only when the URL carries `?obkey=<token>`
 * matching `NEXT_PUBLIC_OB_PREVIEW_KEY`. That gives product a permanent,
 * bookmarkable prod preview URL, and lets the token gate be exercised on a Vercel
 * preview deploy before it ships (DUP-1861).
 *
 * SECURITY: this is an unlisted-link control, NOT a real secret. The check runs
 * client-side, so the token ships in the JS bundle (visible via view-source) and,
 * because it rides in the URL, it also lands anywhere URLs are recorded: PostHog's
 * `$current_url` on the `$pageview` we still capture (autocapture is off but
 * pageview capture is not), plus CDN/server access logs. It's acceptable ONLY
 * because the harness touches nothing sensitive: no real auth, no DB writes, no
 * real newsletter subscribe (preview mode skips all of it). Worst case if the token
 * leaks: someone sees a mock onboarding UI with static sample data. Do NOT reuse
 * this pattern for anything that touches real user data or privileged APIs, and
 * rotate `NEXT_PUBLIC_OB_PREVIEW_KEY` if it ever leaks.
 *
 * Reads `window.location` at call time (not module load) so it reflects the live
 * URL. The `NEXT_PUBLIC_*` reads are inlined at build (webpack DefinePlugin), not
 * read at runtime — keeping them inside the function only matters for tests, where
 * Jest leaves `process.env` live.
 */
export function isOnboardingPreviewUnlocked(): boolean {
  if (typeof window === 'undefined') return false

  // Local dev (`next dev`) → open, no token needed. Vercel builds (preview AND
  // production) run with NODE_ENV='production', so they fall through to the token
  // check below — which is what lets the gate be tested on a preview deploy.
  if (process.env.NODE_ENV === 'development') return true

  // Preview + production: require the unlisted token. Unset env ⇒ hard off (never
  // let an empty/undefined key match an empty `?obkey=` param).
  const key = process.env.NEXT_PUBLIC_OB_PREVIEW_KEY
  if (!key) return false

  return new URLSearchParams(window.location.search).get('obkey') === key
}
